import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Seo from './Seo';

const solutions = [
  {
    label: 'Direct Sourcing',
    desc: 'Access top-tier IT talent directly — eliminating intermediaries and reducing time-to-hire by up to 60%.',
    route: '/solutions/direct',
    highlights: ['Private talent community', 'VMS-ready workflow', 'Lower supplier dependency']
  },
  {
    label: 'IT Consulting',
    desc: 'Strategic advisory services that align your technology investments with long-term business objectives.',
    route: '/solutions/it',
    highlights: ['Architecture modernization', 'Cloud and security strategy', 'Execution governance']
  },
  {
    label: 'Strategic Staffing',
    desc: 'Flexible, scalable workforce solutions to meet project deadlines and operational demands without compromise.',
    route: '/solutions/contingent',
    highlights: ['Fast role fulfillment', 'Compliance-first onboarding', 'Scale up or down quickly']
  },
  {
    label: 'Full-Time Placements',
    desc: 'Rigorous candidate screening and cultural fit assessment to deliver permanent hires that exceed expectations.',
    route: '/solutions/full-time',
    highlights: ['Executive and niche hiring', 'Retention-focused selection', 'Post-join support']
  },
  {
    label: 'Business Process Outsourcing',
    desc: 'Offload non-core functions to our expert teams and redirect your energy toward growth and innovation.',
    route: '/solutions/bpo',
    highlights: ['SLA-based operations', 'Domain-trained delivery teams', 'Continuous improvement model']
  },
  {
    label: 'Contract Positions',
    desc: 'Deploy project-ready contract professionals quickly for time-bound initiatives and specialized delivery needs.',
    route: '/solutions/contract',
    highlights: ['Rapid specialist onboarding', 'Contract-to-hire flexibility', 'Payroll and compliance support']
  },
];

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7 } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const SolutionsOverview = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen pt-4 pb-12">
      <Seo
        title="Solutions"
        description="Libsys staffing and consulting solutions: direct sourcing, strategic staffing, full-time placements, contract positions, IT consulting, and business process outsourcing."
        path="/solutions"
      />
      <section className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={stagger}
          className="text-center mx-auto mb-16"
        >
          <motion.span variants={fadeUp} className="text-red-tagline block mb-1">
            Our Solutions
          </motion.span>
          <motion.h1 variants={fadeUp} className="text-h2 font-black text-primary tracking-tight leading-tight mb-1">
            Tailored Solutions for Diverse Sectors
          </motion.h1>
          <motion.p variants={fadeUp} className="text-slate-600 max-w-2xl mx-auto font-medium leading-relaxed text-small">
            Whether you need permanent talent, a flexible contingent workforce, or end-to-end process outsourcing, Libsys delivers the right solution with precision and speed.
          </motion.p>
        </motion.div>
 
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch"
        >
          {solutions.map((sol, i) => (
            <motion.article
              key={i}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              className="relative overflow-hidden border border-[#236086]/20 bg-[#2e79a8] p-5 lg:p-6 hover:shadow-2xl transition-all rounded-2xl flex flex-col group shadow-lg"
            >
              <div className="absolute inset-0 opacity-[0.15] bg-[radial-gradient(circle_at:18%_18%,white_0,transparent_44%)] pointer-events-none" />
              <h3 className="relative z-10 text-[20px] font-black text-white leading-tight mb-2 tracking-tight">
                {sol.label}
              </h3>
              <p className="relative z-10 text-blue-50/90 text-subtext leading-relaxed font-medium mb-4 max-w-[98%]">
                {sol.desc}
              </p>
              <ul className="relative z-10 mb-4 space-y-2">
                {sol.highlights.map((point) => (
                  <li key={point} className="text-white text-tiny font-bold leading-relaxed flex items-start gap-2.5">
                    <span className="w-1.2 h-1.2 rounded-full bg-white/40 mt-1.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
              <Link
                to={sol.route}
                className="relative z-10 mt-auto inline-flex items-center gap-2 text-white font-black text-[10.5px] uppercase tracking-wider hover:gap-3 transition-all active:scale-95 bg-white/10 w-fit px-5 py-2.5 rounded-xl hover:bg-white/20"
              >
                Explore Solution <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.article>
          ))}
        </motion.div>
      </section>
    </div>
  );
};

export default SolutionsOverview;
